import {
  Button,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useState, type ReactNode } from 'react';

/**
 * Minimal shape of a scan whose access can be managed. Both the overview
 * "most recent scan" card and the scans table pass their own richer scan
 * objects, which structurally satisfy this.
 */
export interface ManageableScan {
  id: string;
  expiresAt: string | null;
  revokedAt?: string | null;
}

interface UseGuestAccessActionsParams {
  hotelId?: string;
  userId?: string;
}

interface GuestAccessActions {
  /** Open the "adjust expiration" dialog for a scan. */
  openAdjust: (scan: ManageableScan) => void;
  /** Open the "revoke access" confirmation for a scan. */
  openRevoke: (scan: ManageableScan) => void;
  /** True while an expiration update is in flight. */
  isUpdating: boolean;
  /** True while a revoke is in flight. */
  isRevoking: boolean;
  /**
   * The dialogs for both flows. Render once per surface (e.g. at the end of the
   * tab) so the triggers have somewhere to open into.
   */
  dialogs: ReactNode;
}

function formatDate(dateString: string | null): string {
  if (!dateString) return 'No expiration';
  return format(new Date(dateString), 'MMM d, yyyy');
}

/**
 * Shared access-management actions for a guest's access-code scans.
 *
 * Centralises the "adjust expiration" and "revoke access" mutations plus their
 * dialogs so every surface that manages a guest's access (the overview card,
 * the scans table) behaves identically and stays in sync. Each consumer renders
 * its own trigger (a button, a menu item) and drops {@link GuestAccessActions.dialogs}
 * into its tree.
 *
 * @param params - The hotel and user the scans belong to, used to invalidate the
 *   right queries after a mutation so every tab refreshes.
 * @returns Imperative openers, in-flight flags, and the dialog elements to render.
 */
export function useGuestAccessActions({
  hotelId,
  userId,
}: UseGuestAccessActionsParams): GuestAccessActions {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const [adjustScan, setAdjustScan] = useState<ManageableScan | null>(null);
  const [revokeScan, setRevokeScan] = useState<ManageableScan | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['scanner-history', hotelId, userId] });
    queryClient.invalidateQueries({ queryKey: ['Scanned User'] });
  };

  const updateExpirationMutation = useMutation({
    mutationFn: ({ scanId, expiresAt }: { scanId: string; expiresAt: string }) =>
      apiClient.patch(`/hotel-access-code-scans/${scanId}`, { expiresAt }),
    onSuccess: () => {
      toast.success('Expiration date updated');
      invalidate();
      setAdjustScan(null);
      setSelectedDate(undefined);
    },
    onError: () => {
      toast.error('Failed to update expiration date');
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (scanId: string) =>
      apiClient.post(`/hotel-access-code-scans/${scanId}/revoke`, {}),
    onSuccess: () => {
      toast.success('Access revoked');
      invalidate();
      setRevokeScan(null);
    },
    onError: () => {
      toast.error('Failed to revoke access');
    },
  });

  const openAdjust = (scan: ManageableScan) => {
    setSelectedDate(scan.expiresAt ? new Date(scan.expiresAt) : new Date());
    setAdjustScan(scan);
  };

  const closeAdjust = () => {
    setAdjustScan(null);
    setSelectedDate(undefined);
  };

  const handleSaveExpiration = () => {
    if (!adjustScan || !selectedDate) return;
    updateExpirationMutation.mutate({
      scanId: adjustScan.id,
      expiresAt: selectedDate.toISOString(),
    });
  };

  const handleConfirmRevoke = () => {
    if (!revokeScan) return;
    revokeMutation.mutate(revokeScan.id);
  };

  const dialogs = (
    <>
      {/* Adjust expiration */}
      <Dialog open={adjustScan !== null} onOpenChange={(open) => !open && closeAdjust()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Expiration</DialogTitle>
            <DialogDescription>
              Change when this guest&apos;s access from this scan expires.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {adjustScan?.expiresAt && (
              <p className="text-sm text-muted-foreground">
                Current expiration:{' '}
                <span className="font-medium text-foreground">
                  {formatDate(adjustScan.expiresAt)}
                </span>
              </p>
            )}
            <DatePicker date={selectedDate} onDateChange={setSelectedDate} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeAdjust}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveExpiration}
              disabled={!selectedDate || updateExpirationMutation.isPending}
            >
              {updateExpirationMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke access */}
      <Dialog open={revokeScan !== null} onOpenChange={(open) => !open && setRevokeScan(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke Access</DialogTitle>
            <DialogDescription>
              This will immediately expire access for this scan. The guest will no longer have
              access through this scan entry.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeScan(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmRevoke}
              disabled={revokeMutation.isPending}
            >
              {revokeMutation.isPending ? 'Revoking...' : 'Revoke'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  return {
    openAdjust,
    openRevoke: setRevokeScan,
    isUpdating: updateExpirationMutation.isPending,
    isRevoking: revokeMutation.isPending,
    dialogs,
  };
}
